import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { useSession } from "../auth/session";
import { beginLogin, isSsoConfigured } from "../auth/oidc";
import { fetchIdpHint, useTenantBranding } from "../auth/tenantBranding";
import { respondPath, withReturnTo } from "../auth/questionnaireReturnTo";

// THE MAILED QUESTIONNAIRE LINK'S LANDING, and the one pre-session route that is not a sign-in page
// (task f71bf1eb; the sign-in page it used to forward to is gone as of task 09e495b1).
//
// WHY IT EXISTS. A questionnaire invitation or reminder is the one mail this product sends to a person who
// may have no live session at all: an external learner's email-link session lasts 8 h
// (`AuthOptions.EmailLink.SessionLifetimeMinutes`) and lives in one browser, so following the link days
// later, or from the phone the mail was read on, is the ordinary case. The link used to be
// `{PublicBaseUrl}/questionnaires/{id}/respond` — an apex-only shell path — so `RequireAuth` bounced such a
// learner to a bare `/login`, where (production serves every tenant from the apex, ADR-0018) no tenant
// could be resolved. The mail now carries `/t/{slug}/questionnaires/{id}/respond`
// (`QuestionnaireLinks.RespondUrl`) and this route is what that prefix buys.
//
// WHY A FORWARDER AND NOT THE FORM ITSELF. The authenticated shell is mounted at `/` only, and keeping it
// that way is ADR-0018 §6.3's decision rather than an accident (see the header of
// ../auth/tenantSelection.ts): after sign-in the tenant is STORED state, not a path segment, so a second
// mount of the whole shell under `/t/:slug/` would give every in-app URL two spellings and the `X-Tenant`
// header two sources. So the slug does its one job — naming the organisation while nobody is signed in —
// and is then dropped:
//
//   • WITH a session, straight to the apex form. Nothing is lost by dropping the slug: the learner's
//     `clsk_` token carries `tenant_id` and resolves on the apex with no hint at all, which
//     `TenantHostDoorTests.An_external_learners_session_resolves_its_own_tenant_on_the_apex_with_no_hint`
//     pins. A `/t/<slug>/` URL for a tenant that is not this session's would be refused by the door, and
//     refusing it here as well would only duplicate that rule in a place that cannot enforce it.
//     The session may of course be a DEAD one that storage still holds — the 8 h revoke, as it really
//     happens — and the apex form's first call then 401s. That bounce keeps the organisation now:
//     ../auth/session.tsx remembers the ending session's slug and `RequireAuth` addresses the sign-in page
//     at it, which is the other half of task 09e495b1 (see below).
//   • WITHOUT one, STRAIGHT TO THE TENANT'S IDENTITY PROVIDER — no sign-in page in between.
//
// WHY THE SIGN-IN PAGE IS GONE (owner's decision, 3.11.2026). Until this task an anonymous arrival was
// forwarded to `/t/<slug>/login`, which offers two doors — "kirjaudu organisaation tunnuksilla" and the
// external learner's email link — and asks the person to recognise which is theirs. Since task ec54522c
// an external learner is invited with a PERSONAL sign-in link (`/t/<slug>/login/email/redeem?token=…`),
// so the tokenless link this route answers is the link of somebody who signs in with their organisation's
// account. For them the choice has exactly one right answer, and offering two was measured in production
// to send the owner down the wrong one. So this route calls `beginLogin` itself, with the slug as the
// tenant and the respondent form as `returnTo`: the person meets their own provider's credential page and
// comes back on the form.
//
// WHAT HAPPENS TO AN SSO RECIPIENT WHO HAS NO ACCOUNT. Nothing in this app: the refusal belongs to the
// realm (or, for a federated customer, to their own directory), which renders its own "invalid username or
// password" and never redirects back. No session is established, no token is minted and no page of this
// SPA sees the attempt, so the state they are left in is the realm's login page — which is also where
// someone whose password has merely expired is put, and the realm is the only party that can tell those
// two apart. The way OUT for a person who has no organisation account at all is the link below.
//
// THE WAY OUT, and why it is a link and not a second button. A reminder mailed before ec54522c, or a link
// forwarded by hand, can still put an external learner on this route, and the identity provider has
// nothing to offer them. "Etkö kirjaudu organisaatiosi tunnuksilla?" takes them to
// `/t/<slug>/login/email` — THIS organisation's email-link request, carrying the questionnaire as the
// destination, so the link they are mailed opens the form. It is visually subordinate on purpose: this is
// the exception, and a second equally-weighted door is the thing the owner's decision removes.
//
// AND WHY THAT LINK IS REACHABLE AT ALL. `beginLogin` navigates the document away, so a link only this
// render drew would be unusable. The hand-off is therefore recorded per TAB
// (`sessionStorage`, like the PKCE verifier it is about to write), and a SECOND arrival on the same form —
// which is what Back from the provider's page is — draws the page WITHOUT redirecting again. Without that
// the external learner is in a loop: forward to the provider, Back, forward again, with the escape never
// on screen long enough to click. The organisation button is offered beside it in that state, because a
// person who came back by mistake must still be able to go on.
const HANDOFF_KEY = "clms.questionnaireSsoHandoff";

/** Whether THIS TAB has already handed `form` to the identity provider. Never throws (private modes). */
function handedOff(form: string): boolean {
  try {
    return sessionStorage.getItem(HANDOFF_KEY) === form;
  } catch {
    return false;
  }
}

/** Record the hand-off, so Back from the provider draws the page instead of bouncing again. */
function rememberHandoff(form: string): void {
  try {
    sessionStorage.setItem(HANDOFF_KEY, form);
  } catch {
    // A refused write costs one extra redirect on Back, which is what the code did before this guard —
    // never a reason to withhold the sign-in the person came for.
  }
}

export function TenantRespondEntry() {
  const { slug = "", id = "" } = useParams();
  const { session } = useSession();
  const { t, i18n } = useTranslation();
  const branding = useTenantBranding();

  // `encodeURIComponent` on both: the params come out of a URL somebody was mailed, and a slug or an id
  // carrying a `/` or a `?` would otherwise rewrite the path it is being pasted into. The route pattern
  // already constrains neither — matching is by segment, not by shape. `respondPath` is the one spelling
  // of the form's path (../auth/questionnaireReturnTo), shared with the mailed redeem link's `returnTo`.
  const form = respondPath(id);
  const tenantRoot = `/t/${encodeURIComponent(slug)}`;
  // The escape hatch keeps the destination as well as the organisation, exactly as the redeem page's
  // refusals do: `withReturnTo` appends only a value that is really a respond path.
  const emailLinkPath = withReturnTo(`${tenantRoot}/login/email`, form);

  // Read ONCE, on this document's first render: a hand-off recorded by `handOff` below must not flip this
  // render into the "you came back" state while the browser is already leaving for the provider.
  const cameBack = useRef(handedOff(form));
  // …and at most ONE automatic hand-off per document, which StrictMode makes load-bearing rather than
  // tidy: it invokes a mount effect twice, and two `beginLogin`s race — the second writes its own PKCE
  // verifier and state over the first's while the browser may already be committing the first's
  // authorization URL, so the code comes back against a state nobody stashed and the callback refuses it.
  // The sessionStorage marker above cannot stand in for this: it is about the NEXT document.
  const startedHandOff = useRef(false);

  const handOff = useCallback(() => {
    rememberHandoff(form);
    const locale = i18n.language.split("-")[0];
    // `kc_idp_hint` is resolved from the same unauthenticated tenant lookup this page already made, and
    // AWAITED rather than read off `branding`: a federated customer's broker is `hideOnLogin`, so a
    // hint-less authorization request lands their people on a realm page that does not offer their own
    // provider (ADR-0014 §12 C-6, ../auth/tenantBranding `fetchIdpHint`).
    void fetchIdpHint(slug).then((idpHint) =>
      beginLogin({ tenant: slug, returnTo: form, locale, idpHint }),
    );
  }, [form, i18n.language, slug]);

  // WHETHER TO HAND OFF BY ITSELF. Not while the tenant lookup is in flight (the hint would be dropped,
  // and with it a federated customer's whole sign-in), not for an organisation that does not exist, not
  // for a tab that has already been sent once, and not with a session in hand. A lookup that FAILED still
  // hands off, with no hint — the same call `fetchIdpHint` documents, because a reachable realm page is
  // the better half of a sign-in to keep when the branding read falls over.
  const handOffNow =
    !session
    && isSsoConfigured()
    && !cameBack.current
    && (branding.status === "ready" || branding.status === "error");

  useEffect(() => {
    if (!handOffNow || startedHandOff.current) return;
    startedHandOff.current = true;
    handOff();
  }, [handOffNow, handOff]);

  // `replace`, so the forwarder leaves no history entry: the mailed URL is the entry point, and Back from
  // the form must leave the app rather than bounce through here again.
  if (session) return <Navigate to={form} replace />;

  // No IdP configured — local development and the guard harness, where there is nothing to redirect to.
  // The sign-in page's own placeholder-session fallback is the only way in there, so forward to it exactly
  // as this route did before the owner's decision.
  if (!isSsoConfigured()) {
    return <Navigate to={`${tenantRoot}/login?returnTo=${encodeURIComponent(form)}`} replace />;
  }

  // An organisation that does not exist: a terminal state, and no redirect. Sending this arrival to the
  // realm would ask a person to authenticate at a tenant nobody runs, and the slug it names is the one
  // thing this page must not guess around — `POST /api/auth/email-link/request` is deliberately silent
  // about which organisations exist (ADR-0013 §4), so a forwarder that improvised a tenant would hand
  // that answer out at the hop before it.
  if (branding.status === "unknown") {
    return (
      <AuthLayout>
        <div className="auth__head">
          <h1 className="auth__title" data-testid="login-unknown-tenant">
            {t("states.unknownTenant")}
          </h1>
          <p className="auth__text" role="alert">
            {t("auth.unknownTenantBody")}
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="auth__head">
        <h1 className="auth__title">{t("auth.questionnaireSignInTitle")}</h1>
        <p className="auth__status" role="status">
          <span className="auth__spinner" aria-hidden="true" />
          {t("auth.questionnaireSignInBody")}
        </p>
      </div>

      {cameBack.current && (
        <div className="auth__actions">
          <button
            type="button"
            className="btn btn--primary btn--block"
            data-testid="respond-sso-again"
            onClick={handOff}
          >
            {t("auth.loginWithOrg")}
          </button>
        </div>
      )}

      <Link className="auth__backlink" to={emailLinkPath} data-testid="respond-no-org-account">
        {t("auth.questionnaireNoOrgAccount")}
      </Link>
    </AuthLayout>
  );
}
